import { useState, useEffect, useRef, type ReactNode } from 'react';
import { noop } from 'lodash';

type Props = {
  callback?: () => void;
  children?: ReactNode;
  loader?: () => (scriptURL: string, onLoaded: () => void) => void;
  preload?: () => void;
  scriptURL?: string;
};

const LOADING_STATE_NOT_LOADED = 'NOT_LOADED';
const LOADING_STATE_STARTED = 'STARTED';
const LOADING_STATE_LOADED = 'LOADED';

export default function AsyncScriptLoader({
  callback = noop,
  children,
  loader = () => noop as () => void,
  preload = noop,
  scriptURL = '',
}: Props) {
  const [loadingState, setLoadingState] = useState(LOADING_STATE_NOT_LOADED);
  const isUnmountedRef = useRef(false);

  useEffect(() => {
    preload();
    setLoadingState(LOADING_STATE_STARTED);

    const load = loader();
    load(scriptURL, () => {
      if (!isUnmountedRef.current) {
        setLoadingState(LOADING_STATE_LOADED);
      }
    });

    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (loadingState === LOADING_STATE_LOADED) {
      callback();
    }
  }, [loadingState, callback]);

  return loadingState === LOADING_STATE_LOADED ? children : null;
}
